import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { USER_CURRENT_KEY } from '../../../api/public-voissa/users';
import { applyThemeForUser } from '../../../libs/theme';

/**
 * ThemeSync — composant invisible (return null) à monter au top-level.
 *
 * Synchronise le thème dès que l'utilisateur est connu, sans dépendre du
 * Menu/ThemeSwitch. Un admin avec localStorage='dark' verra son thème
 * appliqué immédiatement à l'arrivée sur le site.
 *
 * Implémentation : s'abonne directement au QueryCache React Query.
 * Dès que la query [USER_CURRENT_KEY] résout (déclenchée par useQueryCurrentUser
 * ailleurs avec enabled:true), on lit le rôle et on applique le thème.
 *
 * canUseTheme(user) (côté libs/theme.js) bloque les non-admins → light forcé.
 */
const ThemeSync = () => {
    const queryClient = useQueryClient();
    const [role, setRole] = useState(() => {
        const user = queryClient.getQueryData([USER_CURRENT_KEY]);
        return user?.role;
    });

    // Subscribe au cache pour détecter quand la query user résout / change.
    useEffect(() => {
        const cache = queryClient.getQueryCache();
        const unsubscribe = cache.subscribe((event) => {
            const queryKey = event?.query?.queryKey;
            if (!queryKey || queryKey[0] !== USER_CURRENT_KEY) return;
            const user = queryClient.getQueryData([USER_CURRENT_KEY]);
            const newRole = user?.role;
            setRole((prev) => (prev === newRole ? prev : newRole));
        });
        return unsubscribe;
    }, [queryClient]);

    // Applique le thème dès que le rôle change (login, logout, role update).
    useEffect(() => {
        applyThemeForUser({ role });
    }, [role]);

    return null;
};

export default ThemeSync;
